import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The click event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Accordion layout: mobile — open rows with `open-by-default-on-mobile`; desktop — all panels open.
   * Tabs layout (`accordion--tabs`): honour open-by-default attributes; `accordion-tabs.js` keeps one active where tabs apply.
   * Use `data-open-by-default="false"` to keep the panel closed by default on desktop (e.g. dropdowns).
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    const accordionRoot = this.closest('.accordion');
    const isTabsLayout = accordionRoot?.classList.contains('accordion--tabs') ?? false;

    /** Either host flag suffices (Liquid emits both together when the row defaults open). */
    const wantsDefaultOpenPanel =
      this.hasAttribute('open-by-default-on-mobile') || this.hasAttribute('open-by-default-on-desktop');

    if (isMobile) {
      this.details.open = wantsDefaultOpenPanel;
      return;
    }

    if (isTabsLayout) {
      this.details.open = wantsDefaultOpenPanel;
      return;
    }

    this.details.open = this.dataset.openByDefault !== 'false';
  }

  /**
   * Handles keydown events for the accordion.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
